// Четыре крупных экрана: Rating, Shop, Quests, BP.
// Каждый — порт из profile-prototype.html (ui-spec.md §4 Магазин, §6 БП, §7 Рейтинг).

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { ContainerIcon } from '../components/ContainerIcon';
import { useTelegram } from '../telegram/useTelegram';
import { useGameStore } from '../store/useGameStore';
import { listQuests, claimQuest, clickQuest, verifyQuest, type QuestDto,
  getBattlePass, claimBattlePass, purchaseBattlePass,
  getFirewallStatus, buyFirewallApi, type FirewallStatus,
  buyPackApi, buyHeroApi, createStarsInvoice, getPityStatus, type PityStatusResponse } from '../api/client';
import { useToast } from '../components/Toast';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';
import { trTitleText, trLeague } from '../i18n/serverStrings';
// МАГ.1: полный список Героев из @data — для магазина и вкладок фракций.
// @ts-ignore — JS-модули
import { heroes as heroesDeusData } from '@data/heroes-deus.js';
// @ts-ignore
import { heroes as heroesSynData } from '@data/heroes-syntetics.js';
// @ts-ignore
import { heroes as heroesGlbData } from '@data/heroes-global.js';
// @ts-ignore
import { heroes as heroesLivData } from '@data/heroes-livlife.js';
import { HeroAvatar } from '../components/HeroAvatar';
import styles from './MainScreens.module.css';

const FACTION_COLOR: Record<string, string> = {
  deus: 'var(--deus)', syntetics: 'var(--syn)', global: 'var(--gsec)', livlife: 'var(--liv)'
};

// ============================================================
// 1. RATING — ui-spec.md §7
// ============================================================

function buildLeagues(t: (ru: string, en: string) => string) {
  return [
    { ru: 'Трущобы',      name: t('Трущобы', 'Slums'),              streak: 3 as number | null },
    { ru: 'Стритраннеры', name: t('Стритраннеры', 'Streetrunners'), streak: 3 as number | null },
    { ru: 'Нетраннеры',   name: t('Нетраннеры', 'Netrunners'),      streak: 5 as number | null },
    { ru: 'Фиксеры',      name: t('Фиксеры', 'Fixers'),             streak: 5 as number | null },
    { ru: 'Оперативники', name: t('Оперативники', 'Operatives'),    streak: 5 as number | null },
    { ru: 'Корпораты',    name: t('Корпораты', 'Corporates'),       streak: 7 as number | null },
    { ru: 'Высшая лига',  name: t('Высшая лига', 'Big League'),     streak: null as number | null }
  ];
}

// БП.1: лидерборд тянется с сервера через GET /api/leaderboard.
// Этап 70: КОРП ВОЙНЫ — тоже с сервера (топ по USDT-выигрышам).
import { getBigLeagueLeaderboard, getCorpWarsLeaderboard, type BigLeagueEntry, type CorpWarsEntry } from '../api/client';
import { PlayerName } from '../components/PlayerName';

export function RatingScreen() {
  const tr = useTr();
  const [tab, setTab] = useState<'high' | 'war'>('high');
  const [lbHigh, setLbHigh] = useState<BigLeagueEntry[]>([]);
  const [lbWar, setLbWar] = useState<CorpWarsEntry[]>([]);
  const progress = useGameStore(s => s.progress);
  const myProgress = { current: progress.leagueWinStreak, max: progress.leagueWinStreakNeeded };

  // ФТУЕ.6: tooltip-флоу первого входа в Рейтинг (по диздоку §5.5 + §11.4).
  useTooltipFlow('rating', () => [
    {
      id: 'rate-intro',
      coach: {
        title: tr('Лиги · 1/4', 'Leagues · 1/4'),
        body: tr(
          '6 лиг от низшей к высшей: Трущобы → Фиксеры → Нетраннеры → Корпораты → Стритраннеры → Оперативники → Высшая лига. В Рейтинговом бою побеждаешь подряд → растёшь по лигам.',
          '6 leagues from low to high: Slums → Fixers → Netrunners → Corporates → Streetrunners → Operatives → Big League. Win in Ranked back-to-back to climb.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'rate-firewall',
      coach: {
        title: tr('Файервол · 2/4', 'Firewall · 2/4'),
        body: tr(
          'Когда проигрываешь — лига может откатиться. Файервол даёт +1 жизнь: первый проигрыш в лиге не откатит. Получаешь автоматом за подъём + можно купить в Магазине. Один Файервол = один спасённый матч.',
          'When you lose — league can drop. Firewall gives +1 life: first loss in a league won\'t demote you. Earned on promotion + can buy in Shop. One Firewall = one saved match.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'rate-rewards',
      coach: {
        title: tr('Сезонные награды · 3/4', 'Season rewards · 3/4'),
        body: tr(
          'За первое попадание в каждую лигу — разовые награды (Скрипты / ШК / косметика). За завершение сезона на каком-то уровне — сезонные награды по таблице лиг. Сезон длится ~6 недель.',
          'One-time reward for reaching each league for the first time (Scripts / Cipher Crates / cosmetics). Season-end rewards by your final league. A season lasts ~6 weeks.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'rate-leaderboard',
      coach: {
        title: tr('Лидерборды · 4/4', 'Leaderboards · 4/4'),
        body: tr(
          'Топ-50 текущего сезона видны во вкладке «Высшая лига». Параллельно — топ Войны Корпораций (по выигрышам в USDT). Обновляется каждые ~10 минут.',
          'Top-50 of the current season visible on the «Big League» tab. Alongside — Corp Wars top (by USDT winnings). Updates every ~10 minutes.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  useEffect(() => {
    (async () => {
      try {
        // ЛИГ.1.4: лидерборд Высшей лиги (по N = wins - losses).
        const data = await getBigLeagueLeaderboard(50);
        setLbHigh(data);
      } catch {
        // Сервер недоступен или endpoint пустой — оставляем пусто.
      }
    })();
    (async () => {
      try {
        // Этап 70: топ Войны Корпораций (по USDT-выигрышам).
        const data = await getCorpWarsLeaderboard(50);
        setLbWar(data);
      } catch {
        // Сервер недоступен — оставляем пусто.
      }
    })();
  }, []);

  const leagues = buildLeagues(tr);
  // ЛИГ: реальная лига игрока из стора (вместо хардкода «ФИКСЕРЫ / #1 247»).
  const myLeagueRu = progress.league === 'Высшая Лига' ? 'Высшая лига' : progress.league;
  const curIdx = Math.max(0, leagues.findIndex(l => l.ru === myLeagueRu));
  const myLeagueName = leagues[curIdx].name;
  const nextLeague = curIdx < leagues.length - 1 ? leagues[curIdx + 1] : null;
  return (
    <ScreenLayout title={tr('РЕЙТИНГ', 'RATING')} backTo="/home">
      {/* Карточка моей позиции */}
      <div className={`${styles.myRating} cut-l`}>
        <div className={styles.myRatingTop}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
            <path d="M12 3l8 4v6c0 4-3 7-8 8-5-1-8-4-8-8V7z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <div className={styles.myRatingInfo}>
            <div className={styles.myRatingLeague}>{myLeagueName.toUpperCase()}</div>
            <div className={styles.myRatingPlace}>{tr('Место в сезоне', 'Season place')}: <strong>{progress.ratingPlace}</strong></div>
          </div>
        </div>
        {nextLeague ? (
          <div className={styles.streakRow}>
            <span className={styles.streakLabel}>{tr(`До «${nextLeague.name}»: ${myProgress.current} / ${myProgress.max} побед подряд`, `Until «${nextLeague.name}»: ${myProgress.current} / ${myProgress.max} wins in a row`)}</span>
            <div className={styles.streakDots}>
              {Array.from({ length: myProgress.max }).map((_, i) => (
                <div key={i} className={`${styles.streakDot} ${i < myProgress.current ? styles.streakDotOn : ''}`} />
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.streakRow}>
            <span className={styles.streakLabel}>{tr('Высшая лига — выше некуда. Держи топ сезона.', 'Big League — top tier. Hold your season rank.')}</span>
          </div>
        )}
      </div>

      {/* Лестница лиг */}
      <div className={styles.leagueLadder}>
        {leagues.map(l => (
          <div
            key={l.name}
            className={`${styles.leagueRow} ${l.ru === myLeagueRu ? styles.leagueRowCurrent : ''} cut-s`}
          >
            <span className={styles.leagueName}>{l.name}</span>
            {l.streak !== null && (
              <span className={styles.leagueReq}>{l.streak} {tr('побед подряд', 'wins in a row')}</span>
            )}
            {l.streak === null && (
              <span className={styles.leagueReq} style={{ color: 'var(--neon-am)' }}>{tr('топ-30', 'top-30')}</span>
            )}
          </div>
        ))}
      </div>

      {/* Tabs лидербордов */}
      <div className={styles.lbTabs}>
        <button
          className={`${styles.lbTab} ${tab === 'high' ? styles.lbTabActive : ''}`}
          onClick={() => setTab('high')}
        >
          {tr('ВЫСШАЯ ЛИГА', 'BIG LEAGUE')}
        </button>
        <button
          className={`${styles.lbTab} ${tab === 'war' ? styles.lbTabActive : ''}`}
          onClick={() => setTab('war')}
        >
          {tr('КОРП ВОЙНЫ', 'CORP WARS')}
        </button>
      </div>

      <div className={styles.lbList}>
        {tab === 'high' && lbHigh.map(r => (
          <div key={r.id} className={`${styles.lbRow} cut-s`}>
            <span className={`${styles.lbPlace} ${r.place === 1 ? styles.lbPlaceGold : r.place <= 3 ? styles.lbPlaceSilver : ''}`}>#{r.place}</span>
            <div className={styles.lbDot} style={{ background: FACTION_COLOR[r.faction] || '#888' }} />
            <span className={styles.lbNick}>
              <PlayerName
                name={r.display_name}
                accentColor={r.profile_accent_color}
                title={r.active_title}
                size="sm"
                inline
              />
            </span>
            <span className={styles.lbStats}>{r.wins}-{r.losses}</span>
            <span className={styles.lbNet} style={{ color: r.score >= 0 ? 'var(--neon-li)' : '#ff7a7a' }}>
              {r.score >= 0 ? '+' : ''}{r.score}
            </span>
          </div>
        ))}
        {tab === 'war' && lbWar.map(r => (
          <div key={r.id} className={`${styles.lbRow} cut-s`}>
            <span className={`${styles.lbPlace} ${r.place === 1 ? styles.lbPlaceGold : r.place <= 3 ? styles.lbPlaceSilver : ''}`}>#{r.place}</span>
            <div className={styles.lbDot} style={{ background: FACTION_COLOR[r.faction] || '#888' }} />
            <span className={styles.lbNick}>
              <PlayerName
                name={r.display_name}
                accentColor={r.profile_accent_color}
                title={r.active_title}
                size="sm"
                inline
              />
            </span>
            <span className={styles.lbStats}>{r.wars}{tr('м', 'm')}</span>
            <span className={styles.lbNet} style={{ color: 'var(--neon-li)' }}>
              +${(r.won_usdt_cents / 100).toFixed(2)}
            </span>
          </div>
        ))}
        {((tab === 'high' && lbHigh.length === 0) || (tab === 'war' && lbWar.length === 0)) && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--t-md)', fontSize: 12 }}>
            {tr(
              'Лидерборд формируется. Когда игроки сыграют достаточно матчей — топ появится здесь.',
              'Leaderboard is forming. Once players play enough matches — the top will appear here.'
            )}
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}

// ============================================================
// 2. SHOP — ui-spec.md §4
// ============================================================

type ShopFaction = 'deus' | 'syntetics' | 'global' | 'livlife';

// МАГ.2: цены приведены к диздоку v2.9 §2.4. Дополнительно — цена в Скриптах
// для F2P-пути. Игрок выбирает валюту в модалке.
interface ShopPack {
  id: string;
  name: string;
  containerType: string;
  isFactional: boolean;
  priceUsdt: number;
  priceScripts: number;
  bg: string;
}
// ФТУЕ.7: пак-имена строятся по языку, остальные поля статичны.
function buildShopPacks(t: (ru: string, en: string) => string): ShopPack[] {
  return [
    // МАГ.2 fix: подняли базовые паки чтобы premium стал выгоднее.
    // Цены согласованы с обновлённым диздоком §2.4.
    // Этап 74: цены снижены (решение фаундера: ШК от $3). Синхронно с
    // PACK_PRICES в server/src/routes/shop.ts.
    { id: 'p1', name: t('Основной общий', 'Standard common'),               containerType: 'main_common',      isFactional: false, priceUsdt: 3.0, priceScripts: 200, bg: 'linear-gradient(135deg, #14141F, #1A0F1F)' },
    { id: 'p2', name: t('Продвинутый общий', 'Advanced common'),            containerType: 'advanced_common',  isFactional: false, priceUsdt: 4.0, priceScripts: 250, bg: 'linear-gradient(135deg, #14141F, #0F1F1A)' },
    { id: 'p3', name: t('Основной фракционный', 'Standard faction'),        containerType: 'main_faction',     isFactional: true,  priceUsdt: 3.5, priceScripts: 270, bg: 'linear-gradient(135deg, #14141F, #0F1F2A)' },
    { id: 'p4', name: t('Продвинутый фракционный', 'Advanced faction'),     containerType: 'advanced_faction', isFactional: true,  priceUsdt: 5.0, priceScripts: 350, bg: 'linear-gradient(135deg, #14141F, #2A0F1F)' }
  ];
}

// МАГ.1/МАГ.2: все 16 Героев из @data с полными именами + статами и
// текстом способностей (для модалки превью). Поле name берётся из
// `ability_text`, оно структурное (active/trigger/deathrattle/aura).
interface ShopHero {
  id: string;
  name: string;
  faction: ShopFaction;
  priceUsdt: number;
  priceScripts: number;
  archetype: string | null;
  atk: number;
  armor: number;
  hp: number;
  abilityActive: string | null;
  abilityTrigger: string | null;
  abilityDeathrattle: string | null;
  abilityAura: string | null;
  flavor: string | null;
}
// Цены Героев — Этап 74: $8 (было $15, диздок §2.5) или 600 Скриптов.
function mapHeroDataToShopHero(h: any, faction: ShopFaction): ShopHero {
  const at = h.ability_text || {};
  return {
    id: h.id,
    name: h.name_ru,
    faction,
    priceUsdt: 8,
    priceScripts: 600,
    archetype: h.archetype || null,
    atk: h.atk ?? 0,
    armor: h.armor ?? 0,
    hp: h.hp ?? 0,
    abilityActive:     at.active     || null,
    abilityTrigger:    at.trigger    || null,
    abilityDeathrattle: at.deathrattle || null,
    abilityAura:       at.aura       || null,
    flavor: h.flavor_ru || null
  };
}
const HEROES_BY_FACTION_SHOP: Record<ShopFaction, ShopHero[]> = {
  deus:      (heroesDeusData as any[]).filter(h => h.faction === 'deus')     .map(h => mapHeroDataToShopHero(h, 'deus')),
  syntetics: (heroesSynData  as any[]).filter(h => h.faction === 'syntetics').map(h => mapHeroDataToShopHero(h, 'syntetics')),
  global:    (heroesGlbData  as any[]).filter(h => h.faction === 'global')   .map(h => mapHeroDataToShopHero(h, 'global')),
  livlife:   (heroesLivData  as any[]).filter(h => h.faction === 'livlife')  .map(h => mapHeroDataToShopHero(h, 'livlife'))
};
const FACTION_LABEL_SHOP: Record<ShopFaction, string> = {
  deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф'
};
const FACTION_LABEL_SHOP_EN: Record<ShopFaction, string> = {
  deus: 'Deus', syntetics: 'Synthetics', global: 'Global', livlife: 'Liv Life'
};
function factionLabelShop(f: ShopFaction, isEn: boolean): string {
  return isEn ? FACTION_LABEL_SHOP_EN[f] : FACTION_LABEL_SHOP[f];
}

// МАГ.2: содержимое ШК — что может выпасть и в каком количестве.
// Цвета редкостей: обычные — серый, редкие — cyan, эпик — magenta, легендарки — amber.
// ВАЖНО: эти распределения сейчас placeholder для UI-превью. Когда подключим
// реальное открытие ШК с генератором — числа нужно согласовать с балансом.
type RarityKey = 'common' | 'rare' | 'epic' | 'legendary';
const RARITY_COLOR_SHOP: Record<RarityKey, string> = {
  common:    'var(--r-common)',
  rare:      'var(--r-rare)',
  epic:      'var(--r-epic)',
  legendary: 'var(--r-legend)'
};
const RARITY_LABEL_SHOP: Record<RarityKey, string> = {
  common: 'Обычная', rare: 'Редкая', epic: 'Эпик', legendary: 'Легендарка'
};
const RARITY_LABEL_SHOP_EN: Record<RarityKey, string> = {
  common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary'
};
function rarityLabelShop(r: RarityKey, isEn: boolean): string {
  return isEn ? RARITY_LABEL_SHOP_EN[r] : RARITY_LABEL_SHOP[r];
}

// МАГ.2: распределение карт по диздоку v2.9 §9.1.
// Каждый пак содержит N карт: фиксированные гарантии + один «ролл-слот»
// на эпическую/легендарную. Если ролл не сработал — слот превращается в
// дополнительную карту (Обычную или Редкую согласно диздоку).
//
// Pity (диздок §9.2): эпик гарантированно каждый 20-й пак, лег каждый 40-й.
// Счётчик персональный по типу пака, сбрасывается при получении
// эпика/лега из любого источника. Анти-дубль внутри одного пака.
//
// Шансы ролл-слота подобраны так, чтобы при pity 20/40 средний игрок
// получал эпик за ~10-14 паков, легендарку за ~25-33 пака (см. чат-
// расчёт). В продвинутых паках шансы выше — «повышенный» из диздока.
interface PackContents {
  totalCards: number;
  guarantees: { rarity: RarityKey; count: number }[];
  // Шанс на ролл-слот. Сумма Эп+Лег < 100%, остаток — Об/Ред (зависит от пака).
  rollChances: { epic: number; legendary: number };
  // Что выпадает в слоте если эпик/лег не сработал.
  rollFallback: RarityKey | 'common_or_rare';
}
const PACK_CONTENTS: Record<string, PackContents> = {
  // Основной общий ($3 / 200 Скр): 8 карт любой корпы.
  // 5 Об + 2 Ред + 1 ролл-слот. Если ролл не выпал → Об или Ред.
  main_common: {
    totalCards: 8,
    guarantees: [
      { rarity: 'common', count: 5 },
      { rarity: 'rare',   count: 2 }
    ],
    rollChances: { epic: 3, legendary: 0.3 },
    rollFallback: 'common_or_rare'
  },
  // Основной фракционный ($3.5 / 270 Скр): 6 карт корпы.
  // 4 Об + 1 Ред + 1 ролл-слот.
  main_faction: {
    totalCards: 6,
    guarantees: [
      { rarity: 'common', count: 4 },
      { rarity: 'rare',   count: 1 }
    ],
    rollChances: { epic: 5, legendary: 0.5 },
    rollFallback: 'common_or_rare'
  },
  // Продвинутый общий ($4 / 250 Скр): 6 карт любой корпы.
  // 3 Об + 2 Ред + 1 ролл-слот с повышенным шансом.
  advanced_common: {
    totalCards: 6,
    guarantees: [
      { rarity: 'common', count: 3 },
      { rarity: 'rare',   count: 2 }
    ],
    rollChances: { epic: 7, legendary: 1 },
    rollFallback: 'common_or_rare'
  },
  // Продвинутый фракционный ($5 / 350 Скр): 5 карт корпы.
  // 2 Об + 2 Ред + 1 ролл-слот с повышенным шансом. Fallback → Редкая.
  advanced_faction: {
    totalCards: 5,
    guarantees: [
      { rarity: 'common', count: 2 },
      { rarity: 'rare',   count: 2 }
    ],
    rollChances: { epic: 10, legendary: 1.5 },
    rollFallback: 'rare'
  }
};

export function ShopScreen() {
  const { hapticTap, hapticError, hapticSuccess, alert } = useTelegram();
  const navigate = useNavigate();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const shopPacks = buildShopPacks(tr);

  // ФТУЕ.6: при первом входе в Магазин — обзор-флоу из 3 coach-mark.
  useTooltipFlow('shop', () => [
    {
      id: 'shop-intro',
      coach: {
        title: tr('Магазин', 'Shop'),
        body: tr(
          'Здесь покупаешь Шифроконтейнеры (ШК) за USDT или Скрипты, а также Героев. Внутри ШК — карты разной редкости.',
          'Here you buy Cipher Crates (CC) for USDT or Scripts, plus Heroes. Inside CCs — cards of different rarities.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'shop-pack-types',
      coach: {
        title: tr('Типы ШК', 'CC types'),
        body: tr(
          'Основной (8 карт) — дешевле, базовая выдача. Продвинутый (6 карт) — выше шанс на Эпик / Легу. Фракционные — только карты конкретной корпы.',
          'Standard (8 cards) — cheaper, base drops. Advanced (6 cards) — higher Epic / Legendary chance. Faction CCs — only cards from a specific corp.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'shop-pity',
      coach: {
        title: tr('Pity-механика', 'Pity system'),
        body: tr(
          'Если 20 паков подряд без Эпика — следующий гарантированно даст. 40 без Леги → Лега. Счётчики раздельные по «Основным» и «Продвинутым».',
          '20 packs in a row without an Epic — next one guarantees it. 40 without Legendary → Legendary. Counters are separate for «Standard» and «Advanced».'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  const toast = useToast(s => s.show);
  const usdt = useGameStore(s => s.currencies.usdt);
  const scripts = useGameStore(s => s.currencies.scripts);
  const heroesOwned = useGameStore(s => s.heroesOwned);
  // Этап 74: buyForScripts/addInventoryItems здесь больше не нужны — паки и
  // Герои покупаются ЧЕРЕЗ СЕРВЕР (buy-pack/buy-hero), баланс приходит синком.
  const buyForUsdt = useGameStore(s => s.buyForUsdt); // файервол: локальное зеркало
  const buyHero = useGameStore(s => s.buyHero);       // мгновенный показ купленного
  const addFirewall = useGameStore(s => s.addFirewall);

  // МАГ.1: серверный учёт файерволов за сезон.
  const [fwStatus, setFwStatus] = useState<FirewallStatus | null>(null);
  // МАГ.3: pity-статус игрока по всем типам паков.
  // Ключи containerType: main_common / advanced_common / main_<faction> / advanced_<faction>.
  const [pityStatus, setPityStatus] = useState<PityStatusResponse | null>(null);
  const refreshPity = useCallback(async () => {
    try { setPityStatus(await getPityStatus()); } catch { /* offline */ }
  }, []);
  useEffect(() => {
    (async () => {
      try { setFwStatus(await getFirewallStatus()); } catch { /* offline */ }
    })();
    refreshPity();
  }, [refreshPity]);

  // МАГ.2: модалка превью ШК. Один state на все паки — внутри модалка
  // сама определяет нужен ли выбор фракции.
  const [previewPack, setPreviewPack] = useState<ShopPack | null>(null);
  // МАГ.2: модалка превью Героя (статы + способности + кнопка купить).
  const [previewHero, setPreviewHero] = useState<ShopHero | null>(null);

  // МАГ.1: текущая вкладка фракции в каталоге Героев.
  const [heroTab, setHeroTab] = useState<ShopFaction>('deus');

  // ЗВЁЗДЫ.1 (Этап 75): оплата Telegram Stars. Курс 50⭐ ≈ $1 (синхронно
  // с STARS_PER_USD_CENT на сервере — там цена в ⭐ считается из центов).
  const starsPrice = (priceUsdt: number) => Math.max(1, Math.round(priceUsdt * 50));

  // Открыть инвойс внутри Mini App. Вне Telegram (десктоп-браузер) —
  // обычная ссылка (оплатить там нельзя, но и не падаем).
  const openStarsInvoice = (link: string, onPaid: () => void) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wa = (window as any).Telegram?.WebApp;
    if (wa?.openInvoice) {
      wa.openInvoice(link, (status: string) => { if (status === 'paid') onPaid(); });
    } else {
      window.open(link, '_blank');
    }
  };

  // Выдача после оплаты асинхронная (через бота, обычно <2 сек) —
  // подтягиваем профиль с сервера пару раз с задержкой.
  const syncProfileSoon = () => {
    const syncOnce = async () => {
      try {
        const { getProfile } = await import('../api/client');
        const profile = await getProfile();
        useGameStore.getState().syncFromServer(profile);
      } catch { /* offline */ }
    };
    setTimeout(syncOnce, 1500);
    setTimeout(syncOnce, 4500);
  };

  // МАГ.2: вместо немедленной покупки — открываем превью с содержимым.
  const buyPack = (p: ShopPack) => () => {
    hapticTap();
    setPreviewPack(p);
  };

  // Этап 74: покупка пака ТОЛЬКО через сервер (/api/shop/buy-pack, обе
  // валюты). Раньше кнопки $ и Скр были клиентской заглушкой (списание в
  // сторе + локальный счётчик инвентаря, слетало при перезагрузке), а
  // серверная покупка пряталась за «тест»-кнопкой и умела только Скрипты.
  const onConfirmPackPurchase = async (p: ShopPack, faction: ShopFaction | null, currency: 'usdt' | 'scripts') => {
    setPreviewPack(null);
    if (p.isFactional && !faction) {
      toast(tr('Выбери корпу', 'Pick a corp'), 'amber');
      return;
    }
    try {
      await buyPackApi(
        p.containerType.startsWith('advanced')
          ? (p.isFactional ? 'advanced_faction' : 'advanced_common')
          : (p.isFactional ? 'main_faction' : 'main_common'),
        currency,
        faction ?? undefined
      );
      hapticSuccess();
      toast(tr(
        `Куплен ШК «${p.name}». Открой его в Инвентаре.`,
        `Bought Cipher Crate «${p.name}». Open it in Inventory.`
      ), 'green', 4500);
      // Обновляем pity-статус и актуальный профиль (балансы + список ШК).
      refreshPity();
      try {
        const { getProfile } = await import('../api/client');
        const profile = await getProfile();
        useGameStore.getState().syncFromServer(profile);
      } catch { /* offline */ }
    } catch (e: any) {
      hapticError();
      const msg = String(e?.message || '');
      if (msg.includes('insufficient_funds')) {
        toast(currency === 'usdt'
          ? tr(`Не хватает USDT (нужно $${p.priceUsdt.toFixed(2)})`, `Not enough USDT (need $${p.priceUsdt.toFixed(2)})`)
          : tr(`Не хватает Скриптов (нужно ${p.priceScripts})`, `Not enough Scripts (need ${p.priceScripts})`), 'red');
      } else {
        toast(tr(`Ошибка: ${msg}`, `Error: ${msg}`), 'red');
      }
    }
  };

  const onBuyFirewall = async () => {
    if (!fwStatus || !fwStatus.next) {
      hapticError();
      if (fwStatus?.exhausted_message) toast(fwStatus.exhausted_message, 'amber', 5000);
      return;
    }
    const price = fwStatus.next.price;
    const tierStr = isEn
      ? (fwStatus.next.tier === 1 ? '1st' : fwStatus.next.tier === 2 ? '2nd' : '3rd')
      : `${fwStatus.next.tier}-й`;
    const label = isEn ? `${tierStr} Firewall` : `${tierStr} Файервол`;
    hapticTap();
    if (!window.confirm(tr(
      `Купить ${label} за $${price.toFixed(2)}?`,
      `Buy ${label} for $${price.toFixed(2)}?`
    ))) return;
    // Снимаем USDT локально (как и в остальных покупках), параллельно фиксим
    // покупку на сервере чтобы счётчик за сезон был правдивым.
    const ok = buyForUsdt(price, () => addFirewall(1));
    if (!ok) {
      hapticError();
      alert(tr(
        `Не хватает USDT. Нужно $${price.toFixed(2)}, есть $${usdt.toFixed(2)}.`,
        `Not enough USDT. Need $${price.toFixed(2)}, have $${usdt.toFixed(2)}.`
      ));
      return;
    }
    try {
      const r = await buyFirewallApi();
      setFwStatus(r);
      hapticSuccess();
      toast(tr(`✓ Куплен ${label}`, `✓ Bought ${label}`), 'green');
    } catch (e: any) {
      // Серверная ошибка — например уже 3 куплено (race condition). Откатывать
      // локальные изменения сложно (USDT уже снят), просто показываем тост.
      hapticError();
      toast(tr(
        `Сервер отклонил: ${e?.message || 'ошибка'}`,
        `Server rejected: ${e?.message || 'error'}`
      ), 'red');
      // Подтянуть актуальный статус.
      try { setFwStatus(await getFirewallStatus()); } catch {}
    }
  };

  // МАГ.2: тап на Герое открывает превью с характеристиками/способностями.
  const openHeroPreview = (h: ShopHero) => () => {
    hapticTap();
    setPreviewHero(h);
  };
  // ЗВЁЗДЫ.1: покупка пака за Stars — берём инвойс у сервера и открываем
  // нативную платёжку Telegram. Выдача — после successful_payment боту.
  const onStarsPack = async (p: ShopPack, faction: ShopFaction | null) => {
    if (p.isFactional && !faction) {
      toast(tr('Выбери корпу', 'Pick a corp'), 'amber');
      return;
    }
    setPreviewPack(null);
    try {
      const r = await createStarsInvoice({
        kind: 'pack',
        pack_type: p.containerType.startsWith('advanced')
          ? (p.isFactional ? 'advanced_faction' : 'advanced_common')
          : (p.isFactional ? 'main_faction' : 'main_common'),
        faction: faction ?? undefined
      });
      openStarsInvoice(r.invoice_link, () => {
        hapticSuccess();
        toast(tr(
          'Оплачено! ШК появится в Инвентаре через пару секунд.',
          'Paid! The crate will appear in Inventory in a couple of seconds.'
        ), 'green', 4500);
        syncProfileSoon();
      });
    } catch (e: any) {
      hapticError();
      toast(tr(`Ошибка Stars: ${e?.message || ''}`, `Stars error: ${e?.message || ''}`), 'red');
    }
  };

  // ЗВЁЗДЫ.1: покупка Героя за Stars.
  const onStarsHero = async (h: ShopHero) => {
    setPreviewHero(null);
    try {
      const r = await createStarsInvoice({ kind: 'hero', hero_id: h.id, label: h.name });
      openStarsInvoice(r.invoice_link, () => {
        hapticSuccess();
        toast(tr(
          `Оплачено! Герой ${h.name} появится через пару секунд.`,
          `Paid! Hero ${h.name} will appear in a couple of seconds.`
        ), 'green', 4500);
        syncProfileSoon();
      });
    } catch (e: any) {
      hapticError();
      const msg = String(e?.message || '');
      if (msg.includes('already_owned')) {
        toast(tr('Этот Герой уже куплен', 'You already own this Hero'), 'amber');
      } else {
        toast(tr(`Ошибка Stars: ${msg}`, `Stars error: ${msg}`), 'red');
      }
    }
  };

  // Этап 74: покупка Героя через сервер (раньше — клиентская заглушка:
  // герой слетал при перезагрузке, баланс на сервере не списывался).
  const onConfirmHeroPurchase = async (h: ShopHero, currency: 'usdt' | 'scripts') => {
    setPreviewHero(null);
    if (heroesOwned.has(h.id)) {
      navigate('/decks');
      return;
    }
    try {
      await buyHeroApi(h.id, currency);
      hapticSuccess();
      toast(tr(`Куплен Герой ${h.name}`, `Bought Hero ${h.name}`), 'green');
      buyHero(h.id); // мгновенно показать в UI; сервер уже записал
      try {
        const { getProfile } = await import('../api/client');
        const profile = await getProfile();
        useGameStore.getState().syncFromServer(profile);
      } catch { /* offline */ }
    } catch (e: any) {
      hapticError();
      const msg = String(e?.message || '');
      if (msg.includes('insufficient_funds')) {
        toast(currency === 'usdt'
          ? tr(`Не хватает USDT (нужно $${h.priceUsdt.toFixed(2)})`, `Not enough USDT (need $${h.priceUsdt.toFixed(2)})`)
          : tr(`Не хватает Скриптов (нужно ${h.priceScripts})`, `Not enough Scripts (need ${h.priceScripts})`), 'red');
      } else if (msg.includes('already_owned')) {
        toast(tr('Этот Герой уже куплен', 'You already own this Hero'), 'amber');
      } else {
        toast(tr(`Ошибка: ${msg}`, `Error: ${msg}`), 'red');
      }
    }
  };

  // Рендер блока файерволов: либо один с надписью «Файервол (N/3 за сезон)»,
  // либо «Файерволы закончились…» если все 3 куплены.
  const renderFirewallBlock = () => {
    if (!fwStatus) {
      return (
        <div className={`${styles.shopFwRow} cut-s`} style={{ boxShadow: 'inset 0 0 0 0.5px var(--line)', color: 'var(--t-lo)' }}>
          <span>{tr('Загрузка…', 'Loading…')}</span>
        </div>
      );
    }
    if (!fwStatus.next) {
      // Все 3 куплены — лорное сообщение.
      return (
        <div
          className={`${styles.shopFwRow} cut-s`}
          style={{
            boxShadow: 'inset 0 0 0 0.5px var(--neon-rd, #ff7a7a)',
            color: 'var(--t-md)',
            fontStyle: 'italic',
            fontSize: 11,
            lineHeight: 1.4,
            padding: '14px 12px',
            cursor: 'default'
          }}
        >
          <span>👁 {fwStatus.exhausted_message}</span>
        </div>
      );
    }
    const tier = fwStatus.next.tier;
    const price = fwStatus.next.price;
    const color = tier === 1 ? 'var(--neon-li)' : tier === 2 ? 'var(--neon-am)' : 'var(--neon-rd, #ff7a7a)';
    return (
      <button
        className={`${styles.shopFwRow} cut-s`}
        onClick={onBuyFirewall}
        style={{ boxShadow: `inset 0 0 0 0.5px ${color}` }}
      >
        <span style={{ color }}>🛡 {tr('Файервол', 'Firewall')} ({fwStatus.bought}/{fwStatus.max} {tr('за сезон', 'this season')})</span>
        <span className={styles.shopFwPrice}>${price.toFixed(2)}</span>
      </button>
    );
  };

  const heroesInTab = HEROES_BY_FACTION_SHOP[heroTab];

  return (
    <ScreenLayout title={tr('МАГАЗИН', 'SHOP')} backTo="/home">
      <div className={styles.sectionTitle}>{tr('ШИФРОКОНТЕЙНЕРЫ', 'CIPHER CRATES')}</div>
      <div className={styles.shopGrid} data-tt="sh-packs">
        {shopPacks.map(p => (
          <button key={p.id} className={`${styles.shopCard} cut-m`} style={{ background: p.bg }} onClick={buyPack(p)}>
            <div className={styles.shopCardIcon}>
              <ContainerIcon type={p.containerType} size={48} />
            </div>
            <div className={styles.shopCardName}>{p.name}</div>
            <div className={styles.shopCardPrice}>${p.priceUsdt.toFixed(2)}</div>
            <div style={{ fontSize: 10, color: 'var(--neon-li)', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
              {tr('или', 'or')} {p.priceScripts} <ScriptsIcon size={11} />
            </div>
          </button>
        ))}
      </div>

      <div className={styles.sectionTitle}>{tr('ФАЕРВОЛЫ ЭТОГО СЕЗОНА', "THIS SEASON'S FIREWALLS")}</div>
      <div className={styles.shopList} data-tt="sh-firewall">
        {renderFirewallBlock()}
      </div>

      <div className={styles.sectionTitle}>{tr('ГЕРОИ', 'HEROES')}</div>
      <div className={styles.shopHeroTabs} data-tt="sh-heroes">
        {(['deus','syntetics','global','livlife'] as ShopFaction[]).map(f => (
          <button
            key={f}
            className={`${styles.shopHeroTab} ${heroTab === f ? styles.shopHeroTabActive : ''}`}
            onClick={() => { hapticTap(); setHeroTab(f); }}
            style={heroTab === f ? { boxShadow: `inset 0 0 0 0.5px ${FACTION_COLOR[f]}`, color: FACTION_COLOR[f] } : undefined}
          >
            {factionLabelShop(f, isEn)}
          </button>
        ))}
      </div>
      <div className={styles.shopHeroes}>
        {heroesInTab.map(h => {
          const owned = heroesOwned.has(h.id);
          return (
            <button key={h.id} className={`${styles.shopHero} cut-m`} onClick={openHeroPreview(h)}>
              <HeroAvatar id={h.id} size={52} faction={h.faction} />
              <div className={styles.shopHeroName}>{h.name}</div>
              <div className={styles.shopHeroPrice} style={{ color: owned ? 'var(--neon-li)' : 'var(--t-hi)' }}>
                {owned ? tr('✓ ЕСТЬ', '✓ OWNED') : `$${h.priceUsdt.toFixed(0)}`}
              </div>
              {!owned && (
                <div style={{ fontSize: 10, color: 'var(--neon-li)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>{tr('или', 'or')} {h.priceScripts} <ScriptsIcon size={11} /></div>
              )}
            </button>
          );
        })}
      </div>

      <button
        className={`${styles.shopCosmeticLink} cut-s`}
        onClick={() => navigate('/shop/cosmetic')}
        data-tt="sh-cosmetic"
      >
        <span>👗 {tr('КОСМЕТИКА', 'COSMETICS')}</span>
        <span className={styles.chev}>›</span>
      </button>

      {/* МАГ.2/3: модалка превью ШК — описание содержимого + выбор корпы
          (для фракционных) + кнопки покупки (USDT / Скрипты, Этап 74: обе
          через сервер). */}
      {previewPack && (
        <PackPreviewModal
          pack={previewPack}
          balanceUsdt={usdt}
          balanceScripts={scripts}
          pityCounters={pityStatus?.counters || {}}
          onClose={() => setPreviewPack(null)}
          onBuy={(faction, currency) => onConfirmPackPurchase(previewPack, faction, currency)}
          onStars={(faction) => onStarsPack(previewPack, faction)}
          starsPrice={starsPrice(previewPack.priceUsdt)}
        />
      )}

      {/* МАГ.2: модалка превью Героя — статы, способности, кнопки купить. */}
      {previewHero && (
        <HeroPreviewModal
          hero={previewHero}
          owned={heroesOwned.has(previewHero.id)}
          balanceUsdt={usdt}
          balanceScripts={scripts}
          onClose={() => setPreviewHero(null)}
          onBuy={(currency) => onConfirmHeroPurchase(previewHero, currency)}
          onStars={() => onStarsHero(previewHero)}
          starsPrice={starsPrice(previewHero.priceUsdt)}
        />
      )}
    </ScreenLayout>
  );
}

// ============================================================
// МАГ.2: модалки превью ШК и Героя
// ============================================================

function PackPreviewModal({ pack, balanceUsdt, balanceScripts, pityCounters, onClose, onBuy, onStars, starsPrice }: {
  pack: ShopPack;
  balanceUsdt: number;
  balanceScripts: number;
  pityCounters: Record<string, { epic: number; leg: number }>;
  onClose: () => void;
  onBuy: (faction: ShopFaction | null, currency: 'usdt' | 'scripts') => void;
  // ЗВЁЗДЫ.1: оплата Telegram Stars (нативная платёжка TG, без кошелька).
  onStars: (faction: ShopFaction | null) => void;
  starsPrice: number;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const contents = PACK_CONTENTS[pack.containerType];
  const [picked, setPicked] = useState<ShopFaction | null>(null);
  const factionOk = !pack.isFactional || !!picked;
  const canBuyUsdt = factionOk && balanceUsdt >= pack.priceUsdt;
  const canBuyScripts = factionOk && balanceScripts >= pack.priceScripts;

  // МАГ.3 fix (Вариант Б): pity-счётчики теперь по тиру (основной /
  // продвинутый), а не по конкретному типу пака. Все ШК тира суммируются
  // в один счётчик — выбор корпы не сбрасывает прогресс.
  const isAdvancedTier =
    pack.containerType === 'advanced_common' ||
    pack.containerType === 'advanced_faction';
  const pityKey = isAdvancedTier ? 'advanced_tier' : 'main_tier';
  const myPity = pityCounters[pityKey] || { epic: 0, leg: 0 };
  const tierLabel = isAdvancedTier ? tr('Продвинутый тир', 'Advanced tier') : tr('Основной тир', 'Standard tier');

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 380,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-cy)',
          padding: 18,
          display: 'flex', flexDirection: 'column', gap: 10,
          maxHeight: '90vh', overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          color: 'var(--neon-cy)', fontSize: 12, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          ► {pack.name.toUpperCase()}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
          <ContainerIcon type={pack.containerType} size={80} />
        </div>

        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--t-md)' }}>
          {contents
            ? (isEn
              ? <>Pack has <strong style={{ color: 'var(--t-hi)' }}>{contents.totalCards} cards</strong>{pack.isFactional ? ' of the chosen corp' : ''}</>
              : <>В паке <strong style={{ color: 'var(--t-hi)' }}>{contents.totalCards} карт</strong>{pack.isFactional ? ' выбранной корпы' : ''}</>)
            : <>{tr('Содержимое скоро будет описано', 'Contents will be described soon')}</>}
        </div>

        {contents && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: '8px 12px',
            background: 'var(--bg, #0A0A12)',
            boxShadow: 'inset 0 0 0 0.5px var(--line)'
          }}>
            <div className="cd" style={{
              fontSize: 9, letterSpacing: '0.12em',
              color: 'var(--t-lo)', marginBottom: 4
            }}>
              {tr('ГАРАНТИРОВАНО', 'GUARANTEED')}
            </div>
            {contents.guarantees.map((g, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 12, padding: '3px 0'
              }}>
                <span style={{ color: RARITY_COLOR_SHOP[g.rarity] }}>
                  ● {rarityLabelShop(g.rarity, isEn)}
                </span>
                <span style={{ color: 'var(--t-hi)', fontWeight: 600 }}>× {g.count}</span>
              </div>
            ))}

            <div className="cd" style={{
              fontSize: 9, letterSpacing: '0.12em',
              color: 'var(--t-lo)', marginTop: 8, marginBottom: 4
            }}>
              {tr('СЛОТ-РОЛЛ (1 карта)', 'ROLL SLOT (1 card)')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: RARITY_COLOR_SHOP.epic }}>● {rarityLabelShop('epic', isEn)}</span>
              <span style={{ color: 'var(--t-md)' }}>{contents.rollChances.epic}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: RARITY_COLOR_SHOP.legendary }}>● {rarityLabelShop('legendary', isEn)}</span>
              <span style={{ color: 'var(--t-md)' }}>{contents.rollChances.legendary}%</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--t-lo)', paddingTop: 2 }}>
              {contents.rollFallback === 'rare'
                ? tr('Если не выпало — Редкая карта', "If it didn't roll — Rare card")
                : tr('Если не выпало — Обычная или Редкая', "If it didn't roll — Common or Rare")}
            </div>
          </div>
        )}

        {/* Pity-блок: гарантия эпика/лега по диздоку §9.2 + текущий прогресс */}
        <div style={{
          padding: '8px 12px',
          background: 'var(--bg, #0A0A12)',
          boxShadow: 'inset 0 0 0 0.5px var(--neon-am)',
          fontSize: 10, lineHeight: 1.5, color: 'var(--t-md)'
        }}>
          <div className="cd" style={{
            fontSize: 9, letterSpacing: '0.12em',
            color: 'var(--neon-am)', marginBottom: 4
          }}>
            {tr('ГАРАНТИИ', 'GUARANTEES')}
          </div>
          <div>● <span style={{ color: RARITY_COLOR_SHOP.epic }}>{tr('Эпик', 'Epic')}</span> — {tr('каждый 20-й пак этого тира', 'every 20th pack of this tier')}</div>
          <div>● <span style={{ color: RARITY_COLOR_SHOP.legendary }}>{tr('Легендарка', 'Legendary')}</span> — {tr('каждый 40-й', 'every 40th')}</div>
          <div style={{ fontSize: 9, color: 'var(--t-lo)', marginTop: 4 }}>
            {tr(
              'Все ШК тира суммируются (покупные, награды квестов, БП, грантов).',
              'All CCs of the tier are counted together (purchases, quest rewards, BP, grants).'
            )}
          </div>
          <div style={{
            marginTop: 6, paddingTop: 6,
            borderTop: '0.5px solid var(--line)',
            color: 'var(--t-md)'
          }}>
            <div style={{ fontSize: 9, color: 'var(--t-lo)', letterSpacing: '0.08em', marginBottom: 2 }}>
              {tierLabel.toUpperCase()} · {tr('общий счётчик', 'global counter')}
            </div>
            <div>
              {tr('Твой прогресс:', 'Your progress:')}
              <span style={{ color: RARITY_COLOR_SHOP.epic, marginLeft: 6 }}>
                {tr('Эп', 'Ep')} {myPity.epic}/20
              </span>
              <span style={{ color: RARITY_COLOR_SHOP.legendary, marginLeft: 8 }}>
                {tr('Лег', 'Leg')} {myPity.leg}/40
              </span>
            </div>
            {(20 - myPity.epic) <= 3 && (
              <div style={{ color: RARITY_COLOR_SHOP.epic, marginTop: 2 }}>
                ✨ {tr(
                  `До гарантии эпика осталось ${20 - myPity.epic} пак(ов)`,
                  `${20 - myPity.epic} pack(s) until guaranteed Epic`
                )}
              </div>
            )}
            {(40 - myPity.leg) <= 5 && (
              <div style={{ color: RARITY_COLOR_SHOP.legendary, marginTop: 2 }}>
                ✨ {tr(
                  `До гарантии лега осталось ${40 - myPity.leg} пак(ов)`,
                  `${40 - myPity.leg} pack(s) until guaranteed Legendary`
                )}
              </div>
            )}
          </div>
        </div>

        {pack.isFactional && (
          <>
            <div className="cd" style={{
              fontSize: 10, letterSpacing: '0.12em',
              color: picked ? 'var(--t-hi)' : 'var(--neon-am)',
              textAlign: 'center', marginTop: 4
            }}>
              {picked
                ? tr(`КОРПА: ${factionLabelShop(picked, false)}`, `CORP: ${factionLabelShop(picked, true).toUpperCase()}`)
                : tr('ВЫБЕРИ КОРПУ', 'PICK A CORP')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {(['deus','syntetics','global','livlife'] as ShopFaction[]).map(f => (
                <button
                  key={f}
                  onClick={() => setPicked(f)}
                  style={{
                    padding: '10px 6px',
                    background: picked === f ? FACTION_COLOR[f] : 'var(--bg)',
                    color: picked === f ? 'var(--bg)' : FACTION_COLOR[f],
                    border: 'none',
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.08em',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    boxShadow: `inset 0 0 0 0.5px ${FACTION_COLOR[f]}`
                  }}
                >
                  {factionLabelShop(f, isEn)}
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
          <button
            onClick={() => canBuyUsdt && onBuy(picked, 'usdt')}
            disabled={!canBuyUsdt}
            style={{
              padding: 12,
              background: canBuyUsdt ? 'var(--neon-li)' : 'var(--bg)',
              color: canBuyUsdt ? 'var(--bg)' : 'var(--t-lo)',
              border: 'none',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.08em',
              cursor: canBuyUsdt ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              boxShadow: canBuyUsdt ? 'none' : 'inset 0 0 0 0.5px var(--line)'
            }}
          >
            ${pack.priceUsdt.toFixed(2)}
          </button>
          <button
            onClick={() => canBuyScripts && onBuy(picked, 'scripts')}
            disabled={!canBuyScripts}
            style={{
              padding: 12,
              background: canBuyScripts ? 'var(--neon-cy)' : 'var(--bg)',
              color: canBuyScripts ? 'var(--bg)' : 'var(--t-lo)',
              border: 'none',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.08em',
              cursor: canBuyScripts ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              boxShadow: canBuyScripts ? 'none' : 'inset 0 0 0 0.5px var(--line)'
            }}
          >
            {pack.priceScripts} <ScriptsIcon size={13} />
          </button>
        </div>
        {/* ЗВЁЗДЫ.1: оплата Telegram Stars — нативная платёжка, без TON. */}
        <button
          onClick={() => factionOk && onStars(picked)}
          disabled={!factionOk}
          style={{
            padding: 12,
            background: factionOk ? 'var(--neon-am, #FFB13D)' : 'var(--bg)',
            color: factionOk ? 'var(--bg)' : 'var(--t-lo)',
            border: 'none',
            fontSize: 12, fontWeight: 700,
            letterSpacing: '0.08em',
            cursor: factionOk ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
            boxShadow: factionOk ? 'none' : 'inset 0 0 0 0.5px var(--line)'
          }}
        >
          ⭐ {starsPrice} · TELEGRAM STARS
        </button>
        <button
          onClick={onClose}
          style={{
            padding: 8, background: 'transparent', color: 'var(--t-md)',
            border: '0.5px solid var(--t-md)',
            fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: 1
          }}
        >{tr('ОТМЕНА', 'CANCEL')}</button>
      </div>
    </div>
  );
}

function HeroPreviewModal({ hero, owned, balanceUsdt, balanceScripts, onClose, onBuy, onStars, starsPrice }: {
  hero: ShopHero;
  owned: boolean;
  balanceUsdt: number;
  balanceScripts: number;
  onClose: () => void;
  onBuy: (currency: 'usdt' | 'scripts') => void;
  // ЗВЁЗДЫ.1: оплата Telegram Stars.
  onStars: () => void;
  starsPrice: number;
}) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const color = FACTION_COLOR[hero.faction];
  const canBuyUsdt = balanceUsdt >= hero.priceUsdt;
  const canBuyScripts = balanceScripts >= hero.priceScripts;
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 380,
          boxShadow: `inset 0 0 0 0.5px ${color}`,
          padding: 18,
          display: 'flex', flexDirection: 'column', gap: 10,
          maxHeight: '90vh', overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          color, fontSize: 10, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          {tr('КОРПА', 'CORP')} {factionLabelShop(hero.faction, isEn).toUpperCase()}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
          <HeroAvatar id={hero.id} size={96} faction={hero.faction} />
        </div>

        <div style={{
          color: 'var(--t-hi)', fontSize: 14, letterSpacing: '0.04em',
          fontWeight: 700, textAlign: 'center', lineHeight: 1.3
        }}>
          {hero.name}
        </div>

        {hero.archetype && (
          <div style={{
            color: 'var(--t-lo)', fontSize: 10, letterSpacing: '0.08em',
            textAlign: 'center', marginTop: -4
          }}>
            {hero.archetype}
          </div>
        )}

        {/* Статы: ATK / ARMOR / HP */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 4, padding: '10px 0',
          background: 'var(--bg, #0A0A12)',
          boxShadow: 'inset 0 0 0 0.5px var(--line)',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: 9, color: 'var(--t-lo)', letterSpacing: '0.1em' }}>{tr('АТК', 'ATK')}</div>
            <div style={{ color: '#ff8e8e', fontSize: 20, fontWeight: 700 }}>{hero.atk}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'var(--t-lo)', letterSpacing: '0.1em' }}>{tr('БРОНЯ', 'ARMOR')}</div>
            <div style={{ color: '#ffd56a', fontSize: 20, fontWeight: 700 }}>{hero.armor}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'var(--t-lo)', letterSpacing: '0.1em' }}>HP</div>
            <div style={{ color: '#74e398', fontSize: 20, fontWeight: 700 }}>{hero.hp}</div>
          </div>
        </div>

        {/* Способности */}
        {hero.abilityActive && (
          <AbilityRow label={tr('АКТИВКА', 'ACTIVE')} text={hero.abilityActive} color="var(--neon-cy)" />
        )}
        {hero.abilityTrigger && (
          <AbilityRow label={tr('ТРИГГЕР', 'TRIGGER')} text={hero.abilityTrigger} color="var(--neon-mg)" />
        )}
        {hero.abilityAura && (
          <AbilityRow label={tr('АУРА', 'AURA')} text={hero.abilityAura} color="var(--neon-li)" />
        )}
        {hero.abilityDeathrattle && (
          <AbilityRow label={tr('ПОСМЕРТИЕ', 'DEATHRATTLE')} text={hero.abilityDeathrattle} color="var(--neon-am)" />
        )}

        {/* Винрейт сезона — заглушка под будущее. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'var(--bg, #0A0A12)',
          fontSize: 11
        }}>
          <span style={{ color: 'var(--t-lo)' }}>{tr('Винрейт сезона', 'Season winrate')}</span>
          <span style={{ color: 'var(--t-lo)', fontStyle: 'italic' }}>{tr('скоро', 'soon')}</span>
        </div>

        {hero.flavor && (
          <div style={{
            color: 'var(--t-lo)', fontSize: 11, lineHeight: 1.5,
            fontStyle: 'italic', textAlign: 'center', padding: '4px 6px'
          }}>
            «{hero.flavor}»
          </div>
        )}

        {owned ? (
          <button
            onClick={() => onBuy('usdt')}
            style={{
              padding: 14,
              background: 'var(--bg)',
              color: 'var(--neon-li)',
              border: 'none',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.12em',
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: 'inset 0 0 0 0.5px var(--neon-li)',
              marginTop: 4
            }}
          >
            {tr('✓ В КОЛОДЫ', '✓ TO DECKS')}
          </button>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
            <button
              onClick={() => canBuyUsdt && onBuy('usdt')}
              disabled={!canBuyUsdt}
              style={{
                padding: 12,
                background: canBuyUsdt ? 'var(--neon-li)' : 'var(--bg)',
                color: canBuyUsdt ? 'var(--bg)' : 'var(--t-lo)',
                border: 'none',
                fontSize: 12, fontWeight: 700,
                letterSpacing: '0.08em',
                cursor: canBuyUsdt ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                boxShadow: canBuyUsdt ? 'none' : 'inset 0 0 0 0.5px var(--line)'
              }}
            >
              ${hero.priceUsdt.toFixed(0)}
            </button>
            <button
              onClick={() => canBuyScripts && onBuy('scripts')}
              disabled={!canBuyScripts}
              style={{
                padding: 12,
                background: canBuyScripts ? 'var(--neon-cy)' : 'var(--bg)',
                color: canBuyScripts ? 'var(--bg)' : 'var(--t-lo)',
                border: 'none',
                fontSize: 12, fontWeight: 700,
                letterSpacing: '0.08em',
                cursor: canBuyScripts ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                boxShadow: canBuyScripts ? 'none' : 'inset 0 0 0 0.5px var(--line)'
              }}
            >
              {hero.priceScripts} <ScriptsIcon size={13} />
            </button>
          </div>
        )}
        {/* ЗВЁЗДЫ.1: оплата Telegram Stars — нативная платёжка, без TON. */}
        {!owned && (
          <button
            onClick={onStars}
            style={{
              padding: 12,
              background: 'var(--neon-am, #FFB13D)',
              color: 'var(--bg)',
              border: 'none',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            ⭐ {starsPrice} · TELEGRAM STARS
          </button>
        )}
        <button
          onClick={onClose}
          style={{
            padding: 8, background: 'transparent', color: 'var(--t-md)',
            border: '0.5px solid var(--t-md)',
            fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: 1
          }}
        >{tr('ЗАКРЫТЬ', 'CLOSE')}</button>
      </div>
    </div>
  );
}

function AbilityRow({ label, text, color }: { label: string; text: string; color: string }) {
  return (
    <div style={{
      padding: '8px 10px',
      background: 'var(--bg, #0A0A12)',
      boxShadow: `inset 0 0 0 0.5px ${color}`
    }}>
      <div className="cd" style={{
        fontSize: 9, letterSpacing: '0.12em', color, marginBottom: 4
      }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--t-hi)', lineHeight: 1.4 }}>
        {text}
      </div>
    </div>
  );
}

// ============================================================
// 3. QUESTS — задания (Этап 46.14/16)
// Подгружаются с сервера через GET /api/quests, создаются в админке.
// ============================================================

function buildCategoryLabels(t: (ru: string, en: string) => string): Record<string, string> {
  return {
    daily:    t('ЕЖЕДНЕВНЫЕ', 'DAILY'),
    weekly:   t('ЕЖЕНЕДЕЛЬНЫЕ', 'WEEKLY'),
    partner:  t('ПАРТНЁРСКИЕ', 'PARTNER'),
    one_time: t('РАЗОВЫЕ', 'ONE-TIME')
  };
}

export function QuestsScreen() {
  const { hapticSuccess, hapticError, hapticTap, alert } = useTelegram();
  const tr = useTr();
  const addScripts = useGameStore(s => s.addScripts);
  const addInventoryItems = useGameStore(s => s.addInventoryItems);

  // ФТУЕ.6: tooltip-флоу первого входа в Задания.
  useTooltipFlow('quests', () => [
    {
      id: 'q-intro',
      coach: {
        title: tr('Задания', 'Quests'),
        body: tr(
          'Здесь — три типа задач: ежедневные (обновляются в 00:00 UTC), еженедельные (понедельник UTC) и партнёрские. За выполнение — Скрипты и ШК.',
          'Three types of tasks here: daily (reset at 00:00 UTC), weekly (Monday UTC), and partner. Rewards — Scripts and Cipher Crates.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'q-click',
      coach: {
        title: tr('Клик-квесты', 'Click quests'),
        body: tr(
          'Часть заданий — внешние ссылки. Тапни «Открыть» → совершишь действие (подписка / переход) → «Проверить». Если задание системой подтвердится — забирай награду.',
          'Some tasks are external links. Tap «Open» → do the action (subscribe / visit) → «Verify». If system confirms — claim the reward.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'q-claim',
      coach: {
        title: tr('Забери награду', 'Claim reward'),
        body: tr(
          'Когда задание выполнено — рядом появится кнопка «Забрать». Если её не нажать, награда копится. Кнопка горит, когда есть что забрать.',
          'When a task is done — a «Claim» button appears next to it. If not pressed — rewards stack up. The button glows when there\'s something to claim.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  const [quests, setQuests] = useState<QuestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Задания которые игрок только что прокликал/проверил — для оптимистичной
  // отрисовки до перезагрузки списка.
  const [optimistic, setOptimistic] = useState<Record<number, Partial<QuestDto>>>({});

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listQuests();
      setQuests(list);
      setOptimistic({});
      setError(null);
    } catch (e: any) {
      setError(e?.message || tr('Не удалось загрузить задания', 'Failed to load quests'));
    } finally {
      setLoading(false);
    }
  }, [tr]);

  useEffect(() => { refresh(); }, [refresh]);

  // Объединяем серверные данные с оптимистичными.
  const view = quests.map(q => ({ ...q, ...(optimistic[q.id] || {}) }));

  const grouped: Record<string, QuestDto[]> = {};
  for (const q of view) {
    (grouped[q.category] ||= []).push(q);
  }

  const onClaim = async (q: QuestDto) => {
    if (q.claimed) return;
    hapticSuccess();
    try {
      const r = await claimQuest(q.id);
      // Обновляем локальные балансы (Zustand) для мгновенного отклика.
      if (r.rewardScripts > 0) addScripts(r.rewardScripts);
      if (r.rewardPacks > 0) addInventoryItems(r.rewardPacks);
      setOptimistic(prev => ({ ...prev, [q.id]: { claimed: true } }));
      const rewardStr = [
        r.rewardScripts > 0 ? `+${r.rewardScripts} ◆` : '',
        r.rewardPacks > 0 ? `+${r.rewardPacks} ${tr('ШК', 'CC')}` : ''
      ].filter(Boolean).join(', ') || tr('спасибо!', 'thanks!');
      alert(tr(`Награда получена: ${rewardStr}`, `Reward received: ${rewardStr}`));
    } catch (e: any) {
      hapticError();
      alert(tr(`Ошибка: ${e?.message || 'не удалось забрать'}`, `Error: ${e?.message || 'failed to claim'}`));
    }
  };

  const onAction = async (q: QuestDto) => {
    hapticTap();
    if (q.completed) {
      onClaim(q);
      return;
    }
    if (q.type === 'click' || q.type === 'external') {
      if (!q.targetUrl) {
        alert(tr('У задания не указан URL', 'Quest has no URL'));
        return;
      }
      // Открываем ссылку через Telegram WebApp openLink / openTelegramLink.
      const tg = (window as any).Telegram?.WebApp;
      const isTgLink = /^https:\/\/t\.me\//i.test(q.targetUrl);
      try {
        if (tg?.openTelegramLink && isTgLink) tg.openTelegramLink(q.targetUrl);
        else if (tg?.openLink) tg.openLink(q.targetUrl);
        else window.open(q.targetUrl, '_blank');
      } catch {
        window.open(q.targetUrl, '_blank');
      }
      // Засчитываем сразу — серверная логика для click/external.
      try {
        await clickQuest(q.id);
        setOptimistic(prev => ({ ...prev, [q.id]: { completed: true, progress: q.targetValue } }));
      } catch (e: any) {
        hapticError();
        alert(tr(`Не удалось засчитать: ${e?.message}`, `Couldn't credit: ${e?.message}`));
      }
      return;
    }
    if (q.type === 'subscribe') {
      if (!q.targetChat) { alert(tr('У задания не указан канал', 'Quest has no channel')); return; }
      // Если ещё не подписан → открыть канал.
      const isFirstTap = q.progress === 0;
      const tg = (window as any).Telegram?.WebApp;
      const url = q.targetChat.startsWith('@')
        ? `https://t.me/${q.targetChat.slice(1)}`
        : `https://t.me/${q.targetChat}`;
      if (isFirstTap) {
        try {
          if (tg?.openTelegramLink) tg.openTelegramLink(url);
          else window.open(url, '_blank');
        } catch { window.open(url, '_blank'); }
        // Помечаем что юзер уже открыл — следующий тап будет verify.
        setOptimistic(prev => ({ ...prev, [q.id]: { progress: 1 } }));
        return;
      }
      // Verify через бэк.
      try {
        await verifyQuest(q.id);
        setOptimistic(prev => ({ ...prev, [q.id]: { completed: true, progress: q.targetValue } }));
      } catch (e: any) {
        hapticError();
        alert(tr(
          'Подписка не найдена. Подпишись и попробуй ещё раз.',
          'Subscription not found. Subscribe and try again.'
        ));
      }
      return;
    }
    // event-задания: просто отображение прогресса, действие — нет.
    if (q.type === 'event') {
      alert(tr(
        'Прогресс растёт автоматически по событиям в игре.',
        'Progress grows automatically from in-game events.'
      ));
    }
  };

  if (loading) {
    return (
      <ScreenLayout title={tr('ЗАДАНИЯ', 'QUESTS')} backTo="/home">
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--t-md)' }}>{tr('Загрузка…', 'Loading…')}</div>
      </ScreenLayout>
    );
  }

  if (error) {
    return (
      <ScreenLayout title={tr('ЗАДАНИЯ', 'QUESTS')} backTo="/home">
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--neon-rd, #ff7a7a)' }}>
          {tr('Ошибка', 'Error')}: {error}
          <br />
          <button onClick={refresh} style={{ marginTop: 12 }}>{tr('Повторить', 'Retry')}</button>
        </div>
      </ScreenLayout>
    );
  }

  if (view.length === 0) {
    return (
      <ScreenLayout title={tr('ЗАДАНИЯ', 'QUESTS')} backTo="/home">
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--t-md)' }}>
          {tr('Активных заданий пока нет.', 'No active quests yet.')}
          <br /><span style={{ fontSize: 11, color: 'var(--t-lo)' }}>{tr(
            'Они появятся когда команда добавит их через админку.',
            'They\'ll appear once the team adds them through the admin panel.'
          )}</span>
        </div>
      </ScreenLayout>
    );
  }

  const orderedCats: string[] = ['daily', 'weekly', 'partner', 'one_time'].filter(c => grouped[c]?.length);
  const categoryLabels = buildCategoryLabels(tr);

  return (
    <ScreenLayout title={tr('ЗАДАНИЯ', 'QUESTS')} backTo="/home">
      {orderedCats.map(cat => (
        <div key={cat}>
          <div className={styles.sectionTitle} data-tt={`qst-${cat}`}>{categoryLabels[cat] || cat}</div>
          <div className={styles.questList}>
            {grouped[cat].map(q => (
              <QuestRow key={q.id} q={q} onAction={() => onAction(q)} />
            ))}
          </div>
        </div>
      ))}
    </ScreenLayout>
  );
}

function questRewardLabel(q: QuestDto, isEn: boolean): string {
  const parts: string[] = [];
  if (q.rewardScripts > 0) parts.push(`${q.rewardScripts} ◆`);
  if (q.rewardPacks > 0) parts.push(`${q.rewardPacks} ${isEn ? 'CC' : 'ШК'}`);
  return parts.join(' + ') || '—';
}

function questCta(q: QuestDto, t: (ru: string, en: string) => string, isEn: boolean): string {
  if (q.claimed) return t('✓ ПОЛУЧЕНО', '✓ CLAIMED');
  if (q.completed) return t('✓ ЗАБРАТЬ', '✓ CLAIM');
  if (q.type === 'click' || q.type === 'external') return t('ОТКРЫТЬ', 'OPEN');
  if (q.type === 'subscribe') return q.progress > 0 ? t('ПРОВЕРИТЬ', 'VERIFY') : t('ПОДПИСАТЬСЯ', 'SUBSCRIBE');
  return questRewardLabel(q, isEn);
}

function QuestRow({ q, onAction }: { q: QuestDto; onAction: () => void }) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const pct = Math.min(100, Math.round((q.progress / q.targetValue) * 100));
  const interactive = !q.claimed;
  // ФТУЕ.7: подставляем EN-версии полей если они заполнены, иначе RU.
  const title = isEn ? (q.titleEn || q.title) : q.title;
  const description = isEn ? (q.descriptionEn || q.description) : q.description;
  return (
    <button
      type="button"
      className={`${styles.questRow} ${q.completed ? styles.questDone : ''} cut-s`}
      onClick={interactive ? onAction : undefined}
      disabled={!interactive}
      style={{ width: '100%', textAlign: 'left', cursor: interactive ? 'pointer' : 'default' }}
      data-tt="qst-row"
    >
      <div className={styles.questInfo}>
        <div className={styles.questName}>{title}</div>
        {description && (
          <div style={{ fontSize: 11, color: 'var(--t-lo)', marginBottom: 4 }}>{description}</div>
        )}
        {q.type === 'event' && (
          <div className={styles.questProgress}>
            <div className={styles.questProgressBar}>
              <div className={styles.questProgressFill} style={{ width: `${pct}%` }} />
            </div>
            <span className={styles.questProgressTxt}>{q.progress}/{q.targetValue}</span>
          </div>
        )}
        {q.type !== 'event' && (
          <div style={{ fontSize: 10, color: 'var(--neon-cy)', letterSpacing: 0.5 }}>
            {q.type === 'click' && tr('ссылка', 'link')}
            {q.type === 'external' && tr('другой бот', 'another bot')}
            {q.type === 'subscribe' && tr('подписка', 'subscribe')}
            {' · ' + tr('награда', 'reward') + ': '}{questRewardLabel(q, isEn)}
          </div>
        )}
      </div>
      <div className={styles.questReward}>{questCta(q, tr, isEn)}</div>
    </button>
  );
}

// ============================================================
// 4. BATTLE PASS — ui-spec.md §6 (БП.1: подключён к серверу)
// ============================================================

interface RewardView {
  type: 'scripts' | 'container' | 'cosmetic' | 'title' | 'perk_unlock';
  text: string;
}

function describeRewardNode(
  reward_type: string,
  payload: any,
  key: number,
  t: (ru: string, en: string) => string
): React.ReactNode {
  if (reward_type === 'scripts') {
    return (
      <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {payload?.amount ?? 0}
        <ScriptsIcon size={12} />
      </span>
    );
  }
  if (reward_type === 'container') {
    const t2 = payload?.container_type || 'main_common';
    const c = payload?.count || 1;
    return (
      <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <ContainerIcon type={t2} size={16} />
        {c > 1 && <span>×{c}</span>}
      </span>
    );
  }
  if (reward_type === 'cosmetic') {
    return <span key={key}>{t('Косметика', 'Cosmetic')} «{payload?.cosmetic_id || ''}»</span>;
  }
  if (reward_type === 'title') {
    // Маппим title_code → EN-имя через runtime-словарь (serverStrings.ts).
    const text = trTitleText(payload?.title_code, payload?.title_text_ru || '');
    return <span key={key}>{t('Титул', 'Title')} «{text}»</span>;
  }
  if (reward_type === 'perk_unlock') {
    // БУГ.14: читабельные имена перков вместо внутренних кодов.
    return <span key={key}>{perkLabel(payload?.perk_code, t)}</span>;
  }
  return <span key={key}>—</span>;
}

// БУГ.14: маппер perk_code → читабельное имя для UI БП.
// Соответствует кодам в server/src/lib/perks.ts (PerkCode).
function perkLabel(code: string | undefined, t: (ru: string, en: string) => string): string {
  switch (code) {
    case 'patron_signature':  return t('Титул «Меценат»',          '«Patron» title');
    case 'support_priority':  return t('Приоритетная поддержка',   'Priority Support');
    case 'unlimited_replays': return t('Безлимитные реплеи',       'Unlimited Replays');
    case 'nickname_color':    return t('Цветной ник',              'Nickname Color');
    case 'deck_slots_x2':     return t('×2 ячеек колод',            '×2 Deck Slots');
    case 'lore_group_access': return t('Доступ к лор-чату',         'Lore Chat Access');
    default:                  return t(`Перк «${code || '?'}»`, `Perk «${code || '?'}»`);
  }
}

function mergeLevel(
  items: Array<{ reward_type: string; payload: any }>,
  t: (ru: string, en: string) => string
): React.ReactNode {
  if (items.length === 0) return null;
  return items.map((it, i) => (
    <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
      {i > 0 && <span style={{ color: 'var(--t-mute)', margin: '0 4px' }}>+</span>}
      {describeRewardNode(it.reward_type, it.payload, i, t)}
    </span>
  ));
}

export function BPScreen() {
  const { hapticSuccess, hapticError, hapticTap, alert } = useTelegram();
  const battlePass = useGameStore(s => s.battlePass);
  const setBattlePass = useGameStore(s => s.setBattlePass);
  const addScripts = useGameStore(s => s.addScripts);
  const tr = useTr();
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);
  const [perksOpen, setPerksOpen] = useState(false);

  // ФТУЕ.6: tooltip-флоу первого входа в Боевой Пропуск.
  useTooltipFlow('bp', () => [
    {
      id: 'bp-intro',
      coach: {
        title: tr('Боевой Пропуск', 'Battle Pass'),
        body: tr(
          '50 уровней наград за сезон. XP получаешь за матчи, задания и активность. Слева — free-дорожка (доступна всем), справа — премиум (открывается покупкой).',
          '50 reward levels per season. XP comes from matches, quests, and activity. Left — free track (open to all), right — premium (unlocks via purchase).'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'bp-rewards',
      coach: {
        title: tr('Награды', 'Rewards'),
        body: tr(
          'Скрипты, ШК, аватарки, эксклюзивные титулы и косметика. Самые сочные награды — на верхних уровнях (40-50). За полное прохождение примерно $50–80 ценности.',
          'Scripts, Cipher Crates, avatars, exclusive titles and cosmetics. The juicy stuff is on top levels (40-50). Full completion ≈ $50–80 in value.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'bp-claim',
      coach: {
        title: tr('Забери награды', 'Claim rewards'),
        body: tr(
          'Когда уровень достигнут — награда мигает. Тапни «Забрать» — пойдёт в инвентарь. Можно копить, но к концу сезона забери всё — иначе сгорит.',
          'When a level is reached — the reward flashes. Tap «Claim» — it goes to inventory. You can stack up, but claim everything by season end — otherwise it burns.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBattlePass();
        setBattlePass(data as any);
      } catch {
        // Сервер недоступен — оставляем null.
      }
    })();
  }, [setBattlePass]);

  if (!battlePass) {
    return (
      <ScreenLayout title={tr('БОЕВОЙ ПРОПУСК', 'BATTLE PASS')} backTo="/home" onHelpOverride={() => setPerksOpen(true)}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t-md)' }}>
          {tr('Загрузка…', 'Loading…')}
        </div>
      </ScreenLayout>
    );
  }

  const { season, progress, catalog, claimed, unlocked_perks, lore_group_invite_url } = battlePass;

  if (!season || !progress) {
    return (
      <ScreenLayout title={tr('БОЕВОЙ ПРОПУСК', 'BATTLE PASS')} backTo="/home" onHelpOverride={() => setPerksOpen(true)}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t-md)' }}>
          {tr('Сейчас нет активного Сезона.', 'No active Season right now.')}<br />{tr('Следи за анонсами в чате.', 'Watch for announcements in the chat.')}
        </div>
      </ScreenLayout>
    );
  }

  const daysLeft = Math.max(0, Math.ceil((new Date(season.ends_at).getTime() - Date.now()) / (24 * 3600 * 1000)));

  // Группируем каталог по level/track.
  const grouped: Record<number, { free: any[]; paid: any[] }> = {};
  for (const c of catalog) {
    grouped[c.level] = grouped[c.level] || { free: [], paid: [] };
    grouped[c.level][c.track].push(c);
  }
  const levels = Object.keys(grouped).map(n => parseInt(n, 10)).sort((a, b) => a - b);
  const claimedSet = new Set(claimed.map(c => `${c.level}:${c.track}`));

  const onPurchase = async () => {
    hapticTap();
    if (!window.confirm(tr(
      'Купить БП за $7.99 с USDT-баланса?',
      'Buy BP for $7.99 from your USDT balance?'
    ))) return;
    setBusy(true);
    try {
      await purchaseBattlePass();
      const data = await getBattlePass();
      setBattlePass(data as any);
      hapticSuccess();
      alert(tr('БП активирован.', 'BP activated.'));
    } catch (e: any) {
      hapticError();
      alert(tr(`Не удалось: ${e?.message || 'ошибка сервера'}`, `Failed: ${e?.message || 'server error'}`));
    } finally {
      setBusy(false);
    }
  };

  const onClaim = async (level: number, track: 'free' | 'paid') => {
    hapticTap();
    setBusy(true);
    try {
      const result = await claimBattlePass(level, track);
      hapticSuccess();
      // Перерисуем БП-стейт.
      const data = await getBattlePass();
      setBattlePass(data as any);
      // ВАЖНО: перетянуть полный профиль, чтобы обновились titles,
      // cosmetics, контейнеры, скрипты и проч. — все награды БП
      // хранятся в разных таблицах, простого setBattlePass недостаточно.
      // Без этого Прозвища (player_titles) и Косметика (player_cosmetics)
      // не появляются в Инвентаре после клейма.
      try {
        const { getProfile } = await import('../api/client');
        const profile = await getProfile();
        useGameStore.getState().syncFromServer(profile);
      } catch {
        // если профиль не подтянулся — fallback на локальный addScripts
      }
      // БУГ.13: alert при успешном claim убран — юзер видит обновлённый
      // UI и hapticSuccess уведомляет о результате без модалки поверх игры.
      force(x => x + 1);
    } catch (e: any) {
      hapticError();
      alert(tr(`Не удалось: ${e?.message || 'ошибка сервера'}`, `Failed: ${e?.message || 'server error'}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenLayout title={tr('БОЕВОЙ ПРОПУСК', 'BATTLE PASS')} backTo="/home" onHelpOverride={() => setPerksOpen(true)}>
      {/* Header: сезон + прогресс */}
      <div className={`${styles.bpHeader} cut-m`}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={styles.bpStatus} data-tt="bp-perks" style={{ color: progress.has_paid ? 'var(--neon-am)' : 'var(--neon-cy)', fontSize: 13 }}>
            {progress.has_paid ? tr('БП АКТИВЕН', 'BP ACTIVE') : tr('F2P (БЕЗ БП)', 'F2P (NO BP)')}
          </div>
          <div className={styles.bpLevel} style={{ fontSize: 15 }} data-tt="bp-level">
            {season.name} — {tr('Уровень', 'Level')}: <strong>{progress.level}</strong> / 100
          </div>
          <div className={styles.bpTimer} style={{ fontSize: 12 }}>{tr('До конца сезона', 'Season ends in')}: {daysLeft} {tr('дн.', 'd')}</div>
          {/* XP bar + % */}
          {(() => {
            const total = progress.xp_in_level + progress.xp_to_next;
            const pct = progress.xp_to_next === 0 ? 100 : Math.floor((progress.xp_in_level / total) * 100);
            return (
              <div data-tt="bp-xp">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--t-md)' }}>{tr(`прогресс до ур. ${Math.min(100, progress.level + 1)}`, `progress to lvl ${Math.min(100, progress.level + 1)}`)}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--neon-cy)' }}>{pct}%</span>
                </div>
                <div style={{
                  marginTop: 4,
                  height: 10,
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <div style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: 'var(--neon-cy)',
                    transition: 'width 200ms'
                  }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--t-md)', marginTop: 4 }}>
                  XP: {progress.xp_in_level} / {total}
                  {progress.level < 100 && progress.xp_to_next > 0 && (
                    <> · {tr('ещё', 'left')} <strong>{progress.xp_to_next}</strong> XP</>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        {!progress.has_paid && (
          <button
            className={`${styles.bpBuy} cd cut-s`}
            onClick={onPurchase}
            disabled={busy}
            data-tt="bp-perks"
          >
            {tr('КУПИТЬ БП', 'BUY BP')}<br />$7.99
          </button>
        )}
      </div>

      {/* Закрытая группа — только при наличии перка.
          ЛОР.1: при тапе шлём POST /api/battle-pass/claim-lore-chat,
          получаем ОДНОРАЗОВУЮ invite-ссылку (TTL 5 мин) и открываем.
          Раньше использовался статический lore_group_invite_url —
          теперь он только индикатор "перк активен", сама ссылка
          выдаётся через endpoint. */}
      {progress.has_paid && unlocked_perks.includes('lore_group_access') && lore_group_invite_url && (
        <div style={{ margin: '10px 0' }}>
          <button
            className="cut-s"
            onClick={async () => {
              hapticTap();
              const { request } = await import('../api/client');
              try {
                const r = await request<{ invite_url: string }>(
                  '/api/battle-pass/claim-lore-chat', { method: 'POST' }
                );
                const url = r.invite_url;
                if (url) {
                  const tg = (window as any).Telegram?.WebApp;
                  if (tg?.openTelegramLink) tg.openTelegramLink(url);
                  else window.open(url, '_blank');
                }
              } catch (e: any) {
                alert(tr(
                  'Не удалось получить ссылку на чат: ' + (e?.message || 'ошибка'),
                  'Failed to get chat link: ' + (e?.message || 'error')
                ));
              }
            }}
            style={{
              width: '100%', padding: '10px',
              background: 'transparent',
              color: 'var(--neon-mg)',
              border: '0.5px solid var(--neon-mg)',
              fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: 1
            }}
          >
            🔓 {tr('ОТКРЫТЬ ЗАКРЫТЫЙ ЧАТ', 'OPEN PRIVATE CHAT')}
          </button>
        </div>
      )}

      <div className={styles.sectionTitle}>{tr('НАГРАДЫ ПО УРОВНЯМ', 'REWARDS BY LEVEL')}</div>
      <div className={styles.bpLevels}>
        <div className={styles.bpLevelsHeader}>
          <span style={{ width: 40 }}>LVL</span>
          <span style={{ flex: 1, textAlign: 'center', color: 'var(--neon-cy)' }}>F2P</span>
          <span style={{ flex: 1, textAlign: 'center', color: progress.has_paid ? 'var(--neon-am)' : 'var(--t-mute)' }}>
            {tr('БП', 'BP')} {progress.has_paid ? '✓' : '🔒'}
          </span>
        </div>
        {levels.map(level => {
          const passed = level <= progress.level;
          const freeItems = grouped[level].free;
          const paidItems = grouped[level].paid;
          const freeClaimed = claimedSet.has(`${level}:free`);
          const paidClaimed = claimedSet.has(`${level}:paid`);
          const freeCanClaim = passed && !freeClaimed && freeItems.length > 0;
          const paidCanClaim = passed && progress.has_paid && !paidClaimed && paidItems.length > 0;
          const isCurrent = level === progress.level;

          return (
            <div
              key={level}
              className={`${styles.bpLevelRow} ${passed ? styles.bpLevelPassed : ''}`}
              style={isCurrent ? { background: 'rgba(0,240,255,0.06)' } : undefined}
            >
              <span className={styles.bpLevelNum}>{level}</span>
              <div className={`${styles.bpReward} ${styles.bpRewardFree} ${passed ? styles.bpRewardOpen : ''}`}
                   style={{ fontSize: 10 }}>
                {mergeLevel(freeItems, tr)}
                {freeCanClaim && (
                  <button
                    onClick={() => onClaim(level, 'free')}
                    disabled={busy}
                    style={claimBtnStyle('cyan')}
                  >{tr('Забрать', 'Claim')}</button>
                )}
                {freeClaimed && <span style={{ marginLeft: 6, color: 'var(--t-md)' }}>✓</span>}
              </div>
              <div className={`${styles.bpReward} ${styles.bpRewardPaid} ${passed && progress.has_paid ? styles.bpRewardOpen : ''}`}
                   style={{ fontSize: 10 }}>
                {paidItems.length > 0 ? mergeLevel(paidItems, tr) : '—'}
                {paidCanClaim && (
                  <button
                    onClick={() => onClaim(level, 'paid')}
                    disabled={busy}
                    style={claimBtnStyle('amber')}
                  >{tr('Забрать', 'Claim')}</button>
                )}
                {paidClaimed && <span style={{ marginLeft: 6, color: 'var(--t-md)' }}>✓</span>}
                {!progress.has_paid && passed && paidItems.length > 0 && !paidClaimed && (
                  <span style={{ marginLeft: 6, color: 'var(--t-mute)' }}>{tr('купи БП', 'buy BP')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Продающая модалка плюшек БП — открывается по «?» в шапке экрана. */}
      {perksOpen && <BpPerksModal onClose={() => setPerksOpen(false)} />}
    </ScreenLayout>
  );
}

// Продающая модалка «Зачем покупать БП». Открывается из BPScreen
// при клике на «?» (onHelpOverride). Скроллится, написано про каждую
// группу плюшек от самого крутого к самому базовому.
function BpPerksModal({ onClose }: { onClose: () => void }) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 250,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 480,
          maxHeight: '90vh',
          boxShadow: 'inset 0 0 0 0.5px rgba(160,170,200,0.18)',
          display: 'flex', flexDirection: 'column'
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '0.5px solid rgba(160,170,200,0.18)'
        }}>
          <h3 style={{ fontSize: 14, letterSpacing: '0.12em', color: 'var(--neon-am)', fontWeight: 700 }}>
            ⚔ {tr('ЗАЧЕМ ПОКУПАТЬ БОЕВОЙ ПРОПУСК', 'WHY BUY THE BATTLE PASS')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--t-md)', fontSize: 24, cursor: 'pointer',
              padding: 0, width: 24
            }}
            aria-label={tr('Закрыть', 'Close')}
          >×</button>
        </header>
        <div style={{ overflowY: 'auto', padding: '14px 16px', flex: 1, fontSize: 13, lineHeight: 1.55 }}>
          <p style={{ color: 'var(--t-md)', fontStyle: 'italic', marginBottom: 14 }}>
            {isEn ? (
              <>Corps — fights for loyalty. The paid BP turns a regular grunt into&nbsp;<span style={{ color: 'var(--neon-am)' }}>someone with weight on the street</span>. Here's what you get:</>
            ) : (
              <>Корпы — это бои за лояльность. Платный БП превращает обычного бойца в&nbsp;<span style={{ color: 'var(--neon-am)' }}>фигуру с весом в районе</span>. Что получаешь:</>
            )}
          </p>

          <PerkRow tier="🏆"
            title={tr('Аватар «Легенда Сезона 1»', 'Avatar «Legend of Season 1»')}
            text={tr(
              'Ур. 100. Эксклюзив навсегда — даже после сезона. Уникальная картинка в твоём профиле и в каждом бою.',
              'Lvl 100. Exclusive forever — even after the season. Unique image in your profile and every battle.'
            )} />
          <PerkRow tier="🎭"
            title={tr('Скин Героя сезона', 'Season Hero skin')}
            text={tr(
              'Ур. 75. Альтернативный портрет твоего бойца — выделяет тебя в матчах против рандомов.',
              "Lvl 75. Alt portrait for your Hero — stands out in matches against rando's."
            )} />
          <PerkRow tier="🃏"
            title={tr('Скин карт сезона', 'Season card skin')}
            text={tr(
              'Ур. 60. Все карты из колоды получают сезонный визуал.',
              'Lvl 60. All cards in the deck get the season visual.'
            )} />
          <PerkRow tier="👤"
            title={tr('3 эксклюзивных аватара + рамки + обложки + эмоции', '3 exclusive avatars + frames + covers + emotes')}
            text={tr(
              'Ур. 30, 60, 90, 99. Чистая косметика — но не повторится в следующих сезонах.',
              'Lvl 30, 60, 90, 99. Pure cosmetics — but it never returns in future seasons.'
            )} />
          <PerkRow tier="🏷"
            title={tr('3 эксклюзивных титула', '3 exclusive titles')}
            text={tr(
              '«Корп Сезона 1», «Высший Сезон 1», «Легенда Сезона 1». Видны под твоим ником везде, где он отображается.',
              '«Corp of Season 1», «Pinnacle Season 1», «Legend of Season 1». Visible under your handle everywhere it shows.'
            )} />
          <PerkRow tier="🌈"
            title={tr('Цветной ник', 'Coloured handle')}
            text={tr(
              'Перк ур. 2. Открывает 7 неон-цветов с эффектом свечения. Тебя теперь видно в любой панели.',
              'Lvl 2 perk. Unlocks 7 neon colours with a glow. You stand out in any panel now.'
            )} />
          <PerkRow tier="💸"
            title={tr('Титул «Меценат»', '«Patron» title')}
            text={tr(
              'Перк ур. 1. Эксклюзивный титул в коллекцию — поставь активным в профиле, и он виден везде: лидерборды, бой, архив.',
              'Lvl 1 perk. Exclusive title for your collection — set it active in profile and it shows everywhere: leaderboards, battle, archive.'
            )} />
          <PerkRow tier="🎬"
            title={tr('Реплеи без лимита', 'Unlimited replays')}
            text={tr(
              'Перк ур. 1. F2P-игроки могут смотреть 1 свой реплей в сутки. С Пропуском — без ограничений + история до 30 дней.',
              'Lvl 1 perk. F2P players see 1 own replay per day. With BP — no limit + 30 days of history.'
            )} />
          <PerkRow tier="🔓"
            title={tr('Доступ в закрытую лор-группу', 'Access to private lore group')}
            text={tr(
              'Перк ур. 5. Telegram-канал с подробностями вселенной Корпов: Корпорации, технологии, события сезонов.',
              'Lvl 5 perk. Telegram channel with Corps universe details: corporations, tech, season events.'
            )} />
          <PerkRow tier="📚"
            title={tr('10 слотов колод вместо 5', '10 deck slots instead of 5')}
            text={tr(
              'Перк ур. 3. Можешь хранить вдвое больше готовых сборок — под каждую фракцию и мету.',
              'Lvl 3 perk. Twice as many saved builds — one per faction or meta.'
            )} />
          <PerkRow tier="🎫"
            title={tr('Приоритет в саппорте', 'Support priority')}
            text={tr(
              'Перк ур. 1. Тикеты от Меценатов идут наверх очереди.',
              'Lvl 1 perk. Patron tickets jump to the top of the queue.'
            )} />
          <PerkRow tier="📦"
            title={tr('5 продвинутых фракционных ШК + 2 продвинутых общих', '5 advanced faction CCs + 2 advanced common')}
            text={tr(
              'Содержимое выше уровня обычных контейнеров — больше шансов на эпики и легендарки.',
              'Higher-tier contents than regular crates — better odds on epics and legendaries.'
            )} />
          <PerkRow tier="◆"
            title={tr('~1500 Скриптов за прохождение', '~1500 Scripts over the season')}
            text={tr(
              'По 15◆ на каждый уровень + бонусы на 25, 40, 70 ур.',
              '15◆ per level + bonuses at lvls 25, 40, 70.'
            )} />

          <div style={{
            marginTop: 18, padding: 12,
            background: 'rgba(255,177,61,0.08)',
            border: '0.5px solid var(--neon-am)',
            color: 'var(--neon-am)',
            fontSize: 12, textAlign: 'center'
          }}>
            <strong>$7.99</strong> {tr('на 45 дней. Не пропусти.', 'for 45 days. Don\'t miss it.')}
          </div>
        </div>
        <footer style={{ padding: 10, borderTop: '0.5px solid rgba(160,170,200,0.18)' }}>
          <button onClick={onClose} style={{
            width: '100%', padding: '10px',
            background: 'transparent',
            color: 'var(--t-md)',
            border: '0.5px solid var(--t-md)',
            fontSize: 12, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: 1
          }}>{tr('ЗАКРЫТЬ', 'CLOSE')}</button>
        </footer>
      </div>
    </div>
  );
}

function PerkRow({ tier, title, text }: { tier: string; title: string; text: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '8px 0',
      borderBottom: '0.5px solid rgba(160,170,200,0.12)'
    }}>
      <div style={{ fontSize: 18, flexShrink: 0, width: 24, textAlign: 'center' }}>{tier}</div>
      <div style={{ flex: 1 }}>
        <div style={{ color: 'var(--neon-cy)', fontWeight: 600, fontSize: 12, marginBottom: 2 }}>{title}</div>
        <div style={{ color: 'var(--t-md)', fontSize: 11, lineHeight: 1.45 }}>{text}</div>
      </div>
    </div>
  );
}

function claimBtnStyle(color: 'cyan' | 'amber'): React.CSSProperties {
  return {
    marginLeft: 6,
    background: 'transparent',
    color: color === 'cyan' ? 'var(--neon-cy)' : 'var(--neon-am)',
    border: `0.5px solid ${color === 'cyan' ? 'var(--neon-cy)' : 'var(--neon-am)'}`,
    fontSize: 9, padding: '2px 6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: 0.5
  };
}
